import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { ReactComponent as FilterAltOutlinedIcon } from '../../../static/icons/filter.svg';
import { Badge, Box, Card, Chip, Grid, Stack, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as React from 'react';
import { getDropRatio } from '../../../services/api-service';
import { WIDGET_REFRESH_INTERVAL } from '../../../utils/constants';
import FilterDrawer from '../../FilterDrawer/FilterDrawer';
import WidgetDownload from '../../Widget-Download/Widget-Download';
import '../HourlyCallSummary/HourlyCallSummary.css';
import useChartResize from '../../../hooks/useChartResize';
import { getLastFetchedDetailFromBrowser } from '../../../services/application-service';
import { ReactComponent as TableIcon } from '../../../static/icons/table.svg';
import { ReactComponent as GraphIcon } from '../../../static/icons/graph.svg';
import OZTable from '../../OZTable/OZTable';

const useStyles: any = makeStyles((theme: Theme) => ({
	boundButton: {
		padding: '5px !important',
		minWidth: 50
	},
	filterItem: {
		fontSize: 12,
		height: 36,
		borderRadius: 20,
		background: 'rgba(0, 142, 255, 0.1)',
		border: '1px solid #008eff',
		padding: `0 ${theme.spacing(2)}`
	},
	itemIndicator: {
		width: 5,
		height: 5,
		borderRadius: 1,
		display: 'inline-block',
		position: 'relative',
		top: '-2px',
		marginRight: 4
	},
	filterItemText: {
		margin: 0,
		color: '#536580'
	},
	filterItemSubText: {
		color: '#99A0A8'
	}
}));

const TableColumns = [
	{ name: 'Campaign Name', key: 'campaignName', align: 'left' },
	{ name: 'Current Drop Ratio (%)', key: 'currentDropRatio', align: 'left' },
	{ name: 'Expected Drop Ratio (%)', key: 'ExpectedDropRatio', align: 'left' }
];


function DropRatio(props: any) {
	const classes = useStyles();
	const { useState, useRef } = React;
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean | undefined>(false);
	const [graphData, setGraphData] = useState<any>([]);
	const [dropRatioFilter, setDropRatioFilter] = useState<any>(props?.selectedFilters?.filters || []);
	const [filterCategory, setFilterCategory] = useState<any>(null);
	const [filterType, setFilterType] = useState<any>(props?.selectedFilters?.filterSection?.id || '1');
	const [filterSection, setFilterSection] = useState<any>(props?.selectedFilters?.filterSection || {});
	const [responseLength, setresponseLength] = useState<Number>(0);
	const downloadContent = useRef<null | any>(null);
	const [campaignName, setCampaignName] = useState<any>([]);
	const [lastFetchedTime, setLastFetchedTime] = useState<any>('');
	const { chartContainerRef, chartDimensions, chartElementRef, redrawChart } = useChartResize();
	const [showGraph, setShowGraph] = useState(true);
	const [rowData, setRowData] = useState<any[]>([]);
	const [columnData] = useState<any[]>(TableColumns);

	const intervalRef = useRef<any>(null);
	const dropRatioLoadingRef = useRef<boolean>(false);

	// const chartElement: any = useRef(null);

	const clearFilter = () => {
		setDropRatioFilter([]);
		props?.handleFilterChange?.([], filterSection, props?.widgetId);
	};

	const handleFilterDelete = (id: Number) => {
		const newFilter = dropRatioFilter.filter((item: any) => item.id !== id);
		setDropRatioFilter(newFilter);
		props?.handleFilterChange?.(newFilter, filterSection, props?.widgetId);
	};

	const filterItems = dropRatioFilter.map((item: any, index: number) => (
		<Chip key={index} label={item.label} color="primary" variant="outlined" onDelete={() => handleFilterDelete(item.id)} deleteIcon={<CloseOutlinedIcon style={{ fontSize: '12px' }} />} />
	));

	const formatGraphData = (data: any) => {
		const graphData: any = {};
		return { graphData };
	};

	const filterHourlyData = (data: any[]) => {
		let filteredItems = data;
		if (dropRatioFilter?.length && filterCategory) {
			const key = `${filterCategory.value}Id`;
			const filterIds = dropRatioFilter.map((filterItem: any) => +filterItem.Id);
			filteredItems = filteredItems.filter((item) => filterIds.includes(item[key]));
		} else {
			filteredItems = filteredItems.filter((item) => item.CampaignId === 0);
		}
		return filteredItems;
	};

	const retriveGraphData = async (filters: any) => {
		try {
			if (dropRatioLoadingRef.current) {
				console.log('Widget data fetch already in progress, skipping this cycle dropRatioLoading');
				return;
			}
			dropRatioLoadingRef.current = true;
			const apiResponse: any = await getDropRatio({
				campaignId: filters
			});
			setRowData(apiResponse);

			const expectedDropRation = apiResponse.map((item: any) => ({
				y: +item.ExpectedDropRatio,
				...item
			}));
			const currentDropRation = apiResponse.map((item: any) => ({
				y: +item.currentDropRatio,
				...item
			}));
			const campaignName = apiResponse.map((item: any) => item.campaignName);
			setCampaignName(campaignName);
			const graphOptions = [
				{
					name: 'Current',
					color: '#4C4C4C',
					type: 'column',
					data: currentDropRation
				},
				{
					name: 'Expected',
					type: 'column',
					color: '#008DFF',
					data: expectedDropRation
				}
			];
			const filteredData = filterHourlyData(apiResponse);
			const { graphData: formattedGraphData } = formatGraphData(filteredData);
			setGraphData(graphOptions);
			// chartElement?.current?.chart?.redraw();
			redrawChart();
			setLastFetchedTime(getLastFetchedDetailFromBrowser());
		}
		catch (error) {
			console.error('Error fetching drop ratio:', error);
		} finally {
			dropRatioLoadingRef.current = false;
		}
	};

	const handlePointClick = (point: any) => {
		// console.log('Clicked point', point);
	};

	const toggleDrawer = (isOpen: boolean) => {
		setIsDrawerOpen(isOpen);
	};

	const handleDownload = (type: string) => {
		console.log(type);
	};

	const options = {
		chart: {
			height: chartDimensions.height,
		},
		credits: { enabled: false },
		title: { text: '' },
		xAxis: {
			labels: {
				rotation: -45,
				style: {
					fontSize: '12px',
					fontFamily: 'Roboto'
				}
			},
			title: {
				text: 'Campaigns ➡',
				style: {
					fontWeight: 'bold'
				}
			},
			categories: campaignName,
			crosshair: true
		},
		yAxis: {
			title: {
				text: 'Drop Ratio  %',
				style: { fontWeight: 'bold' }
			}
		},
		legend: {
			layout: 'horizontal',
			align: 'center',
			verticalAlign: 'top',
			itemMarginTop: 10
		},
		tooltip: {
			formatter(this: any): any {
				return `${this.y}`;
			}
		},
		plotOptions: {
			series: {
				pointWidth: 10,
				groupPadding: 0.4,
				point: {
					events: {
						click: function ({ point }: any) {
							handlePointClick(point);
						}
					}
				}
			}
		},
		series: graphData
	};

	React.useEffect(() => {
		const filterIds = (dropRatioFilter.length && !(responseLength === dropRatioFilter.length)) ? dropRatioFilter.map((item: any) => item.id).join(',') : '0';
		// const filterIds = dropRatioFilter.length ? dropRatioFilter.map((item: any) => item.id).join(',') : '0';
		retriveGraphData(filterIds);
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		intervalRef.current = setInterval(() => retriveGraphData(filterIds), WIDGET_REFRESH_INTERVAL.DROP_RATIO);
		return () => {
			clearInterval(intervalRef.current);
		};
	}, [dropRatioFilter]);

	const handleFilterExistingData = (filteredData: any) => {
		setDropRatioFilter(filteredData);
		props?.handleFilterChange?.(filteredData, filterSection, props?.widgetId);
	};

	const handleApplyFilters = ({ selectedFilters, filterType: newFilterType, filterSection: newfilterSection }: any) => {
		setDropRatioFilter(selectedFilters);
		setFilterType(newFilterType);
		setFilterSection(newfilterSection);
		props?.handleFilterChange?.(selectedFilters, newfilterSection, props?.widgetId);
		toggleDrawer(false);
	};

	return (
		<>
			<Card ref={downloadContent} className="customCard">
				<div className="card-header">
					<div className="card-title">
						Drop Ratio<span className="card-sub-header">(Predictive)</span>
						<span className="card-title-badge">Live</span>
						<span className="last-fetched-time" style={{ marginLeft: '6px' }}>Last updated at {lastFetchedTime} </span>
					</div>
					<div className="card-actions">
						<div className="card-icon" onClick={() => setShowGraph(prev => !prev)}>
							{showGraph ? <TableIcon /> : <GraphIcon />}
						</div>
						{showGraph ? (
							<WidgetDownload element={downloadContent} handleDownload={handleDownload} name={'Drop Ratio'}></WidgetDownload>
						) : (
							<WidgetDownload xlsx={!showGraph} rowData={rowData} columns={columnData} element={downloadContent} handleDownload={handleDownload} name={'Drop Ratio'}></WidgetDownload>
						)}
						<div className="card-icon" onClick={() => toggleDrawer(true)}>
							<Badge badgeContent={dropRatioFilter.length} color="primary">
								<FilterAltOutlinedIcon fontSize="small" style={{ color: '#536580' }} />
							</Badge>
						</div>
					</div>
				</div>
				<Box sx={{ height: "100%", backgroundColor: 'white', }}>
					<Box sx={{ p: 2, pb: 0 }}>
						{dropRatioFilter.length > 0 && (
							<div className="filter-outer">
								<div className="filter-container">
									<Stack direction="row" spacing={1}>
										{filterItems}
									</Stack>
								</div>
								<div className="filter-remove" onClick={clearFilter}>
									Remove
								</div>
							</div>
						)}
					</Box>
					<Box ref={chartContainerRef} sx={{ height: dropRatioFilter.length > 0 ? 'calc(100% - 80px)' : 'calc(100% - 44px)', }}>
						{showGraph ? (
							<HighchartsReact ref={chartElementRef} highcharts={Highcharts} options={options} />
						) : (
							<OZTable columns={columnData} rows={rowData} noMaxHeight={true} height={chartDimensions.height} />
						)}
					</Box>
				</Box>
			</Card>
			<FilterDrawer
				isOpen={isDrawerOpen}
				toggleDrawer={toggleDrawer}
				applyFilters={handleApplyFilters}
				existingSelectedFilters={dropRatioFilter}
				allowedFilters={['Campaign']}
				additionalApiParams={{ campaignType: 'Predictive' }}
				setResponseLength={setresponseLength}
				existingFilterCategory={filterType}
				applyFilterOnExistingData={handleFilterExistingData}
			/>
		</>
	);
}
export default DropRatio;
